import { mergeProps, useRender } from "@base-ui/react";
import { createFormHookContexts, useStore } from "@tanstack/react-form";
import { createContext, useContext, useId } from "react";

import { Label } from "@/components/ui/label";

import { cn } from "../lib/utils";

const {
	fieldContext,
	formContext,
	useFieldContext: useFormFieldContext,
	useFormContext,
} = createFormHookContexts();

type FormItemContextValue = {
	id: string;
};

const FormItemContext = createContext<FormItemContextValue>({} as FormItemContextValue);

function FormItem({ className, ...props }: React.ComponentProps<"div">) {
	const id = useId();

	return (
		<FormItemContext.Provider value={{ id }}>
			<div data-slot="form-item" className={cn("grid gap-2", className)} {...props} />
		</FormItemContext.Provider>
	);
}

const useFieldContext = <T,>() => {
	const { id } = useContext(FormItemContext);
	const { name, store, ...fieldContext } = useFormFieldContext<T>();

	const errors = useStore(store, (state) => state.meta.errors);
	if (!fieldContext) {
		throw new Error("useFieldContext should be used within <FormItem>");
	}

	return {
		id,
		name,
		formItemId: `${id}-form-item`,
		formDescriptionId: `${id}-form-item-description`,
		formMessageId: `${id}-form-item-message`,
		errors,
		store,
		...fieldContext,
	};
};

function FormLabel({ className, ...props }: React.ComponentProps<typeof Label>) {
	const { formItemId, errors } = useFieldContext();

	return (
		<Label
			data-slot="form-label"
			data-error={!!errors.length}
			className={cn("data-[error=true]:text-destructive", className)}
			htmlFor={formItemId}
			{...props}
		/>
	);
}

function FormControl({ render, ...props }: useRender.ComponentProps<"div">) {
	const { errors, formItemId, formDescriptionId, formMessageId } = useFieldContext();
	const defaultProps = {
		"data-slot": "form-control",
		id: formItemId,
		"aria-describedby": !errors.length
			? `${formDescriptionId}`
			: `${formDescriptionId} ${formMessageId}`,
		"aria-invalid": !!errors.length,
	};

	const element = useRender({
		defaultTagName: "div",
		render,
		props: mergeProps<"div">(defaultProps, props),
	});

	return element;
}

function FormDescription({ className, ...props }: React.ComponentProps<"p">) {
	const { formDescriptionId } = useFieldContext();

	return (
		<p
			data-slot="form-description"
			id={formDescriptionId}
			className={cn("text-muted-foreground text-sm", className)}
			{...props}
		/>
	);
}

function FormMessage({ className, ...props }: React.ComponentProps<"p">) {
	const { errors, formMessageId } = useFieldContext();
	const error = errors.at(0);
	const body = errors.length
		? String(typeof error === "string" ? error : (error?.message ?? ""))
		: props.children;
	if (!body) return null;

	return (
		<p
			data-slot="form-message"
			id={formMessageId}
			className={cn("text-destructive text-sm", className)}
			{...props}
		>
			{body}
		</p>
	);
}

export {
	FormItem,
	FormLabel,
	FormControl,
	FormDescription,
	FormMessage,
	fieldContext,
	formContext,
	useFieldContext,
	useFormContext,
};
